
import React, { useState } from 'react';
import { useApiEndpoints, useApiProvider } from '@/hooks/useApiProviders';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Loader2, Plus } from 'lucide-react';
import { EndpointForm } from './EndpointForm';
import { ImportOpenApiSpec } from './ImportOpenApiSpec';

interface EndpointManagerProps {
  providerId: string;
}

export function EndpointManager({ providerId }: EndpointManagerProps) {
  const { data: provider, isLoading: isLoadingProvider } = useApiProvider(providerId);
  const { data: endpoints = [], isLoading: isLoadingEndpoints } = useApiEndpoints(providerId);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedEndpoint, setSelectedEndpoint] = useState<any>(null);
  
  const handleAddEndpoint = () => {
    setSelectedEndpoint(null);
    setIsAddDialogOpen(true);
  };
  
  const handleEditEndpoint = (endpoint: any) => {
    setSelectedEndpoint(endpoint);
    setIsAddDialogOpen(true);
  };
  
  const handleCloseDialog = () => {
    setIsAddDialogOpen(false);
  };
  
  if (isLoadingProvider || isLoadingEndpoints) {
    return (
      <div className="flex items-center justify-center p-6">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  
  const is24SevenProvider = provider?.code === '24seven';
  
  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold">{provider?.name || 'Provider'} Endpoints</h2>
        <Button onClick={handleAddEndpoint}>
          <Plus className="mr-2 h-4 w-4" />
          Add Endpoint
        </Button>
      </div>
      
      {is24SevenProvider && (
        <div className="mb-6">
          <ImportOpenApiSpec providerId={providerId} />
        </div>
      )}
      
      {endpoints.length === 0 ? (
        <Card className="border border-dashed">
          <CardHeader>
            <CardTitle>No Endpoints</CardTitle>
            <CardDescription>
              This provider doesn't have any API endpoints defined yet.
            </CardDescription>
          </CardHeader>
          <CardFooter>
            <Button onClick={handleAddEndpoint} variant="outline">
              <Plus className="mr-2 h-4 w-4" />
              Add your first endpoint
            </Button>
          </CardFooter>
        </Card>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Path</TableHead>
              <TableHead>Method</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {endpoints.map((endpoint) => (
              <TableRow 
                key={endpoint.id}
                className="cursor-pointer hover:bg-muted/50"
                onClick={() => handleEditEndpoint(endpoint)}
              >
                <TableCell className="font-medium">{endpoint.name}</TableCell>
                <TableCell className="font-mono text-sm">{endpoint.path}</TableCell>
                <TableCell>
                  <Badge
                    variant={
                      endpoint.method === 'GET' ? 'default' :
                      endpoint.method === 'POST' ? 'secondary' :
                      endpoint.method === 'PUT' ? 'outline' :
                      'destructive'
                    }
                  >
                    {endpoint.method}
                  </Badge>
                </TableCell>
                <TableCell>
                  {endpoint.is_active !== false ? (
                    <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200">Active</Badge>
                  ) : (
                    <Badge variant="outline" className="bg-gray-100 text-gray-800">Inactive</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditEndpoint(endpoint);
                    }}
                  >
                    Edit
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      
      <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
        <DialogContent className="sm:max-w-[700px]">
          <DialogHeader>
            <DialogTitle>
              {selectedEndpoint ? 'Edit Endpoint' : 'Add Endpoint'}
            </DialogTitle>
          </DialogHeader>
          <EndpointForm 
            endpoint={selectedEndpoint} 
            providerId={providerId}
            onSuccess={handleCloseDialog}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
